/**
 * batch-67 campagnes (FO-502 §2.5) — bezettingsconflicten per klant en week
 * als PURE functie: te veel monteurs van dezelfde klant tegelijk met vakantie
 * in dezelfde ISO-week.
 *
 * Input: per medewerker de vakantieperiode(s) (goedgekeurd of aangevraagd) en
 * de klant(en) waar die medewerker in die week staat ingepland of vast werkt.
 * Output: per (klant, ISO-week) het aantal afwezige medewerkers boven de
 * drempel. Dagen vallen in de ISO-week van hun NL-kalenderdag; de periode is
 * inclusief begin én eind.
 *
 * Geen DB, geen klok: de aanroeper levert de data en de drempel.
 */

import { isoWeek } from './intercompany-periode'

export interface BezettingVakantie {
  employeeId: string
  /** Inclusief, UTC-instants (dag-precisie volstaat). */
  start: Date
  eind: Date
  /** Klant(en) waar deze medewerker in de betreffende weken hoort. */
  clientIds: string[]
}

export interface BezettingConflict {
  clientId: string
  /** ISO-weekjaar + week (bv. 2027-W31). */
  jaar: number
  week: number
  weekLabel: string
  employeeIds: string[]
  aantal: number
  drempel: number
}

const MS_PER_DAG = 86_400_000
const DAG_FMT = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Amsterdam',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})

/** Alle (jaar, week)-sleutels die een periode raakt, op NL-kalenderdagen. */
export function isoWekenVanVakantie(start: Date, eind: Date): Array<{ jaar: number; week: number }> {
  const sleutels = new Map<string, { jaar: number; week: number }>()
  if (eind.getTime() < start.getTime()) return []
  // Loop per dag (vakantieperiodes zijn kort; maximaal enkele weken).
  for (let t = start.getTime(); t <= eind.getTime(); t += MS_PER_DAG) {
    const [y, m, d] = DAG_FMT.format(new Date(t)).split('-').map(Number)
    const w = isoWeek(new Date(Date.UTC(y!, m! - 1, d!)))
    sleutels.set(`${w.jaar}-${w.week}`, w)
  }
  // Ook de einddag zelf als de loop er net overheen stapte (DST).
  const [y, m, d] = DAG_FMT.format(eind).split('-').map(Number)
  const w = isoWeek(new Date(Date.UTC(y!, m! - 1, d!)))
  sleutels.set(`${w.jaar}-${w.week}`, w)
  return [...sleutels.values()].sort((a, b) => a.jaar - b.jaar || a.week - b.week)
}

export function weekLabel(jaar: number, week: number): string {
  return `${jaar}-W${String(week).padStart(2, '0')}`
}

/**
 * Bereken de conflicten. `drempel` = het maximum aantal medewerkers van één
 * klant dat tegelijk weg mag zijn (per week); een conflict ontstaat als het
 * aantal daarboven komt. Optioneel een drempel per klant (wint van de default).
 */
export function berekenBezettingsconflicten(
  vakanties: readonly BezettingVakantie[],
  drempel: number,
  drempelPerKlant: Readonly<Record<string, number>> = {},
): BezettingConflict[] {
  const perKlantWeek = new Map<string, { clientId: string; jaar: number; week: number; employeeIds: Set<string> }>()
  for (const v of vakanties) {
    if (v.clientIds.length === 0) continue
    const weken = isoWekenVanVakantie(v.start, v.eind)
    for (const clientId of new Set(v.clientIds)) {
      for (const w of weken) {
        const key = `${clientId}|${w.jaar}|${w.week}`
        const entry = perKlantWeek.get(key) ?? { clientId, jaar: w.jaar, week: w.week, employeeIds: new Set<string>() }
        entry.employeeIds.add(v.employeeId)
        perKlantWeek.set(key, entry)
      }
    }
  }
  const conflicten: BezettingConflict[] = []
  for (const e of perKlantWeek.values()) {
    const grens = drempelPerKlant[e.clientId] ?? drempel
    if (e.employeeIds.size > grens) {
      conflicten.push({
        clientId: e.clientId,
        jaar: e.jaar,
        week: e.week,
        weekLabel: weekLabel(e.jaar, e.week),
        employeeIds: [...e.employeeIds].sort(),
        aantal: e.employeeIds.size,
        drempel: grens,
      })
    }
  }
  return conflicten.sort((a, b) => a.clientId.localeCompare(b.clientId) || a.jaar - b.jaar || a.week - b.week)
}
